import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, ScrollText, CalendarDays, LogOut, ShieldCheck, PinIcon, Star, Receipt, Building2, HeartHandshake, Menu, MessageSquareWarning, MailWarning, BookOpen, CreditCard, Clock, MapPin, Brain, History } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ADMIN_NAV, POLICY_LIBRARY, ANNOUNCEMENTS, RECEIPTS_BATCH, FAILED_EMAILS_INBOX, BILLING_PAGE, TIMECLOCK, WORK_CENTERS } from "@/constants/testIds";
import { AdvisorFab } from "@/components/AdvisorFab";
import { ProductTourFab } from "@/components/ProductTourFab";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { toast } from "sonner";

const openUsageGuide = async () => {
  try {
    const { data } = await api.get("/static-guides/admin", { responseType: "blob" });
    window.open(URL.createObjectURL(data), "_blank");
  } catch {
    toast.error("No se pudo abrir la guía de uso.");
  }
};

const NAV_ITEMS = [
  { testid: ADMIN_NAV.dashboardLink, to: "/admin", icon: LayoutDashboard, label: "Cumplimiento" },
  { testid: ADMIN_NAV.employeesLink, to: "/admin/empleados", icon: Users, label: "Empleados" },
  { testid: POLICY_LIBRARY.navLink, to: "/admin/politicas", icon: ScrollText, label: "Documentos" },
  { testid: "admin-nav-vacaciones", to: "/admin/vacaciones", icon: CalendarDays, label: "Vacaciones" },
  { testid: ANNOUNCEMENTS.adminNavLink, to: "/admin/avisos", icon: PinIcon, label: "Avisos" },
  { testid: "admin-nav-talento", to: "/admin/talento", icon: Star, label: "Talento" },
  { testid: RECEIPTS_BATCH.navLink, to: "/admin/recibos", icon: Receipt, label: "Recibos" },
  { testid: "admin-nav-correcciones", to: "/admin/correcciones", icon: MessageSquareWarning, label: "Correcciones" },
  { testid: FAILED_EMAILS_INBOX.navLink, to: "/admin/envios-fallidos", icon: MailWarning, label: "Envíos fallidos" },
  { testid: ADMIN_NAV.companyProfileLink, to: "/admin/perfil-empresa", icon: Building2, label: "Perfil de Empresa" },
  { testid: ADMIN_NAV.consultingLink, to: "/admin/consultoria", icon: HeartHandshake, label: "Consultoría" },
  { testid: ADMIN_NAV.nom035Link, to: "/admin/nom-035", icon: Brain, label: "Cumplimiento NOM-035" },
  { testid: TIMECLOCK.navLink, to: "/admin/jornada", icon: Clock, label: "Registro de Jornada" },
  { testid: WORK_CENTERS.navLink, to: "/admin/centros-de-trabajo", icon: MapPin, label: "Centros de Trabajo" },
  { testid: BILLING_PAGE.navLink, to: "/admin/facturacion", icon: CreditCard, label: "Facturación" },
  { testid: ADMIN_NAV.auditLogLink, to: "/admin/bitacora", icon: History, label: "Bitácora de Seguridad" },
];

function NavLinks({ onNavigate }) {
  return (
    <nav className="flex flex-col gap-1 flex-1">
      {NAV_ITEMS.map((item) => (
        <Link
          key={item.to}
          data-testid={item.testid}
          to={item.to}
          onClick={onNavigate}
          className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium hover:bg-white/10 transition-colors"
        >
          <item.icon className="w-4 h-4" strokeWidth={1.5} /> {item.label}
        </Link>
      ))}
    </nav>
  );
}

export function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background flex flex-col lg:flex-row">
      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between bg-petroleum text-bone/90 px-4 py-3">
        <Link to="/admin" className="flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-terracotta" strokeWidth={1.5} />
          <span className="font-heading text-lg font-bold text-white tracking-tight">CERH</span>
        </Link>
        <button
          data-testid={ADMIN_NAV.mobileMenuButton}
          onClick={() => setMobileOpen(true)}
          className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          aria-label="Abrir menú"
        >
          <Menu className="w-6 h-6" strokeWidth={1.5} />
        </button>
      </header>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent
          data-testid={ADMIN_NAV.mobileDrawer}
          side="left"
          closeTestId="admin-mobile-drawer-close"
          className="bg-petroleum text-bone/90 border-none px-6 py-8 w-4/5 flex flex-col overflow-y-auto"
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Menú de navegación</SheetTitle>
            <SheetDescription>Enlaces a las secciones del panel de administración</SheetDescription>
          </SheetHeader>
          <Link to="/admin" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 mb-10">
            <ShieldCheck className="w-7 h-7 text-terracotta" strokeWidth={1.5} />
            <span className="font-heading text-xl font-bold text-white tracking-tight">CERH</span>
          </Link>
          <NavLinks onNavigate={() => setMobileOpen(false)} />
          <div className="border-t border-white/10 pt-4">
            <p className="text-xs text-bone/60 mb-3">{user?.company_name}</p>
            <button onClick={openUsageGuide} className="flex items-center gap-2 text-sm text-bone/80 hover:text-white transition-colors mb-3">
              <BookOpen className="w-4 h-4" strokeWidth={1.5} /> Guía de uso
            </button>
            <button onClick={handleLogout} className="flex items-center gap-2 text-sm text-bone/80 hover:text-white transition-colors">
              <LogOut className="w-4 h-4" strokeWidth={1.5} /> Cerrar sesión
            </button>
          </div>
        </SheetContent>
      </Sheet>

      <aside data-testid={ADMIN_NAV.sidebar} className="hidden lg:flex lg:w-64 flex-col fixed h-screen overflow-y-auto bg-petroleum text-bone/90 px-6 py-8">
        <Link to="/admin" data-testid="admin-sidebar-home-link" className="flex items-center gap-2 mb-10 hover:opacity-80 transition-opacity">
          <ShieldCheck className="w-7 h-7 text-terracotta" strokeWidth={1.5} />
          <span className="font-heading text-xl font-bold text-white tracking-tight">CERH</span>
        </Link>
        <NavLinks />
        <div className="border-t border-white/10 pt-4">
          <p className="text-xs text-bone/60 mb-3">{user?.company_name}</p>
          <button data-testid={ADMIN_NAV.usageGuideButton} onClick={openUsageGuide} className="flex items-center gap-2 text-sm text-bone/80 hover:text-white transition-colors mb-3">
            <BookOpen className="w-4 h-4" strokeWidth={1.5} /> Guía de uso
          </button>
          <button data-testid={ADMIN_NAV.logoutButton} onClick={handleLogout} className="flex items-center gap-2 text-sm text-bone/80 hover:text-white transition-colors">
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> Cerrar sesión
          </button>
        </div>
      </aside>
      <main className="flex-1 lg:ml-64 p-4 sm:p-6 lg:p-10">{children}</main>
      <AdvisorFab role="admin" />
      <ProductTourFab role="admin" />
    </div>
  );
}
